import {useState, useEffect, useCallback, useMemo} from "react";
import {Timestamp} from "firebase/firestore";
import firestoreService, {
  FirestoreSession,
  FirestoreCharacter,
  FirestoreTitan,
} from "../services/firebase/firestore";
import {convertToDate} from "../utils/dateUtils";
import {UserProfile, UserRole} from "../services/firebase/auth";
import {TitanService} from "../services/api/titanService";
import {SessionService} from "../services/api/sessionService";
import {
  createCharacter,
  updateCharacter,
} from "../services/api/charactersService";
import {collection, query, where, onSnapshot} from "firebase/firestore";
import {firestore} from "../services/firebase/config";

// Generic hook for real-time data
export const useFirestoreCollection = <T>(
  collectionName: string,
  queryFn?: () => Promise<T[]>
) => {
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      if (!queryFn) {
        throw new Error(
          `No query function provided for collection: ${collectionName}`
        );
      }
      const result = await queryFn();
      setData(result);
    } catch (err) {
      // Error handling for collection fetch operations
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  }, [collectionName, queryFn]);

  useEffect(() => {
    fetchData();
  }, [fetchData]); // Include fetchData in dependencies

  const refetch = useCallback(() => {
    fetchData();
  }, [fetchData]);

  return {data, loading, error, refetch};
};

// Sessions hooks
export const useSessions = () => {
  const queryFn = useCallback(() => firestoreService.getSessions(), []);
  return useFirestoreCollection<FirestoreSession>("sessions", queryFn);
};

export const useSessionsByStatus = (status: FirestoreSession["status"]) => {
  const [data, setData] = useState<FirestoreSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSessions = async () => {
      try {
        setLoading(true);
        setError(null);
        const sessions = await firestoreService.getSessionsByStatus(status);
        setData(sessions);
      } catch (err) {
        setError(err instanceof Error ? err.message : "An error occurred");
      } finally {
        setLoading(false);
      }
    };

    fetchSessions();
  }, [status]);

  return {data, loading, error};
};

export const useUpcomingSessions = () => {
  return useSessionsByStatus("upcoming");
};

export const useAvailableSessions = () => {
  return useSessionsByStatus("available");
};

export const useCompletedSessions = () => {
  return useSessionsByStatus("completed");
};

export const useSessionsByDM = (dmId: string) => {
  const [data, setData] = useState<FirestoreSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!dmId) {
      setData([]);
      setLoading(false);
      return;
    }

    const fetchSessions = async () => {
      try {
        setLoading(true);
        setError(null);
        const sessions = await firestoreService.getSessionsByDM(dmId);
        setData(sessions);
      } catch (err) {
        setError(err instanceof Error ? err.message : "An error occurred");
      } finally {
        setLoading(false);
      }
    };

    fetchSessions();
  }, [dmId]);

  return {data, loading, error};
};

export const useSessionsByTitan = (titanId: string) => {
  const [data, setData] = useState<FirestoreSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!titanId) {
      setData([]);
      setLoading(false);
      return;
    }

    const fetchSessions = async () => {
      try {
        setLoading(true);
        setError(null);
        const sessions = await firestoreService.getSessionsByTitan(titanId);
        setData(sessions);
      } catch (err) {
        setError(err instanceof Error ? err.message : "An error occurred");
      } finally {
        setLoading(false);
      }
    };

    fetchSessions();
  }, [titanId]);

  return {data, loading, error};
};

// Characters hooks
export const useCharacters = () => {
  const queryFn = useCallback(() => firestoreService.getCharacters(), []);
  return useFirestoreCollection<FirestoreCharacter>("characters", queryFn);
};

export const useCharactersByPlayer = (playerId: string) => {
  const [data, setData] = useState<FirestoreCharacter[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!playerId) {
      setData([]);
      setLoading(false);
      return;
    }

    const fetchCharacters = async () => {
      try {
        setLoading(true);
        setError(null);
        const characters =
          await firestoreService.getCharactersByPlayer(playerId);
        setData(characters);
      } catch (err) {
        setError(err instanceof Error ? err.message : "An error occurred");
      } finally {
        setLoading(false);
      }
    };

    fetchCharacters();
  }, [playerId]);

  return {data, loading, error};
};

export const useCharactersByLevel = (minLevel: number, maxLevel: number) => {
  const [data, setData] = useState<FirestoreCharacter[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCharacters = async () => {
      try {
        setLoading(true);
        setError(null);
        const characters = await firestoreService.getCharactersByLevel(
          minLevel,
          maxLevel
        );
        setData(characters);
      } catch (err) {
        setError(err instanceof Error ? err.message : "An error occurred");
      } finally {
        setLoading(false);
      }
    };

    fetchCharacters();
  }, [minLevel, maxLevel]);

  return {data, loading, error};
};

export const useCharactersByPlayerRealtime = (playerId: string | null) => {
  const [data, setData] = useState<FirestoreCharacter[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!playerId) {
      setData([]);
      setLoading(false);
      setError(null);
      return;
    }

    let unsubscribe: (() => void) | undefined;

    try {
      // Create a query for characters by player
      const charactersQuery = query(
        collection(firestore, "characters"),
        where("playerId", "==", playerId)
      );

      unsubscribe = onSnapshot(
        charactersQuery,
        (snapshot) => {
          const characters = snapshot.docs.map((doc) => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              // Ensure numeric fields are properly converted
              level:
                typeof data.level === "string"
                  ? parseInt(data.level, 10)
                  : data.level,
              mementoTokens:
                typeof data.mementoTokens === "string"
                  ? parseInt(data.mementoTokens, 10)
                  : data.mementoTokens,
              downtimeDays:
                typeof data.downtimeDays === "string"
                  ? parseInt(data.downtimeDays, 10)
                  : data.downtimeDays,
              sessionsPlayed:
                typeof data.sessionsPlayed === "string"
                  ? parseInt(data.sessionsPlayed, 10)
                  : data.sessionsPlayed,
            } as FirestoreCharacter;
          });

          setData(characters);
          setLoading(false);
          setError(null);
        },
        (error) => {
          // Error handling for character snapshot operations
          setError(error.message || "Failed to load characters");
          setLoading(false);
        }
      );
    } catch (err) {
      // Error handling for character listener setup operations
      setError(
        err instanceof Error
          ? err.message
          : "Failed to set up characters listener"
      );
      setLoading(false);
    }

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [playerId]);

  return {data, loading, error};
};

// Players hooks
export const usePlayers = () => {
  const queryFn = useCallback(() => firestoreService.getPlayers(), []);
  return useFirestoreCollection<UserProfile>("players", queryFn);
};

export const useActivePlayers = () => {
  const queryFn = useCallback(() => firestoreService.getActivePlayers(), []);
  return useFirestoreCollection<UserProfile>("players", queryFn);
};

// DMs hooks
export const useDMs = () => {
  const queryFn = useCallback(() => firestoreService.getDMs(), []);
  return useFirestoreCollection<UserProfile>("dms", queryFn);
};

export const useActiveDMs = () => {
  const queryFn = useCallback(() => firestoreService.getActiveDMs(), []);
  return useFirestoreCollection<UserProfile>("dms", queryFn);
};

// Users hooks
export const useUsers = () => {
  const queryFn = useCallback(() => firestoreService.getUsers(), []);
  return useFirestoreCollection<UserProfile>("users", queryFn);
};

export const useActiveUsers = () => {
  const queryFn = useCallback(() => firestoreService.getActiveUsers(), []);
  return useFirestoreCollection<UserProfile>("users", queryFn);
};

export const useUsersByRole = (role: UserRole) => {
  const [data, setData] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        setError(null);
        const users = await firestoreService.getUsersByRole(role);
        setData(users);
      } catch (err) {
        setError(err instanceof Error ? err.message : "An error occurred");
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [role]);

  return {data, loading, error};
};

// Titans hooks
export const useTitans = () => {
  const queryFn = useCallback(() => firestoreService.getTitans(), []);
  return useFirestoreCollection<FirestoreTitan>("titans", queryFn);
};

export const useActiveTitans = () => {
  const queryFn = useCallback(() => firestoreService.getActiveTitans(), []);
  return useFirestoreCollection<FirestoreTitan>("titans", queryFn);
};

export const useTitansByDM = (dmId: string) => {
  const [data, setData] = useState<FirestoreTitan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!dmId) {
      setData([]);
      setLoading(false);
      return;
    }

    const fetchTitans = async () => {
      try {
        setLoading(true);
        setError(null);
        const titans = await firestoreService.getTitansByDM(dmId);
        setData(titans);
      } catch (err) {
        setError(err instanceof Error ? err.message : "An error occurred");
      } finally {
        setLoading(false);
      }
    };

    fetchTitans();
  }, [dmId]);

  return {data, loading, error};
};

// Real-time hooks
export const useSessionsRealtime = () => {
  const [data, setData] = useState<FirestoreSession[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = firestoreService.onSessionsSnapshot((sessions) => {
      setData(sessions);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  return {data, loading, error: null};
};

export const useCharactersRealtime = () => {
  const [data, setData] = useState<FirestoreCharacter[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    try {
      unsubscribe = firestoreService.onCharactersSnapshot((characters) => {
        setData(characters);
        setLoading(false);
        setError(null);
      });
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to set up characters listener"
      );
      setLoading(false);
    }

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  return {data, loading, error};
};

export const usePlayersRealtime = () => {
  const [data, setData] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = firestoreService.onPlayersSnapshot((players) => {
      setData(players);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  return {data, loading, error: null};
};

export const useUsersRealtime = () => {
  const [data, setData] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = firestoreService.onUsersSnapshot((users) => {
      setData(users);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  return {data, loading, error: null};
};

export const useTitansRealtime = () => {
  const [data, setData] = useState<FirestoreTitan[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = firestoreService.onTitansSnapshot((titans) => {
      setData(titans);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  return {data, loading, error: null};
};

export const useTitansByDMRealtime = (dmId: string) => {
  const [data, setData] = useState<FirestoreTitan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!dmId) {
      setData([]);
      setLoading(false);
      setError(null);
      return;
    }

    let unsubscribe: (() => void) | undefined;

    try {
      unsubscribe = firestoreService.onTitansByDMSnapshot(dmId, (titans) => {
        setData(titans);
        setLoading(false);
        setError(null);
      });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to set up titans listener"
      );
      setLoading(false);
    }

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [dmId]);

  return {data, loading, error};
};

// Operation hooks
export const useSessionOperations = () => {
  const createSession = useCallback(
    async (
      session: Omit<FirestoreSession, "id" | "createdAt" | "updatedAt">
    ) => {
      try {
        return await SessionService.createSession({
          name: session.name,
          description: session.description,
          titanId: session.titanId,
          date:
            session.date instanceof Timestamp
              ? session.date.toDate()
              : session.date,
          location: session.location,
          maxPlayers: session.maxPlayers,
          dmId: session.dmId,
          levelRange: session.levelRange,
        });
      } catch (error) {
        // Error handling for session creation operations
        throw error;
      }
    },
    []
  );

  const updateSession = useCallback(
    async (id: string, updates: Partial<FirestoreSession>) => {
      try {
        const updateData: any = {sessionId: id};

        // Convert Timestamp to Date for date field
        if (updates.date && updates.date instanceof Timestamp) {
          updateData.date = updates.date.toDate();
        }

        // Add other fields
        Object.keys(updates).forEach((key) => {
          if (key !== "date") {
            updateData[key] = (updates as any)[key];
          }
        });

        await SessionService.updateSession(updateData);
      } catch (error) {
        throw error;
      }
    },
    []
  );

  const deleteSession = useCallback(async (id: string) => {
    try {
      await SessionService.deleteSession({sessionId: id});
    } catch (error) {
      throw error;
    }
  }, []);

  return {createSession, updateSession, deleteSession};
};

export const useCharacterOperations = () => {
  const createCharacterOperation = useCallback(
    async (
      character: Omit<FirestoreCharacter, "id" | "createdAt" | "updatedAt">
    ) => {
      try {
        const result = await createCharacter({
          name: character.name,
          species: character.species,
          class: character.class,
          level: character.level,
          inventory: character.inventory || [],
          sheetUrl: character.sheetUrl,
          will: character.will,
        });

        if (result.success) {
          return result.characterId;
        } else {
          throw new Error(result.message || "Failed to create character");
        }
      } catch (error) {
        throw error;
      }
    },
    []
  );

  const updateCharacterOperation = useCallback(
    async (id: string, updates: Partial<FirestoreCharacter>) => {
      try {
        const result = await updateCharacter({
          characterId: id,
          updates: {
            name: updates.name,
            species: updates.species,
            class: updates.class,
            level: updates.level,
            inventory: updates.inventory,
            sheetUrl: updates.sheetUrl,
            status: updates.status,
            will: updates.will,
          },
        });

        if (!result.success) {
          throw new Error(result.message || "Failed to update character");
        }
      } catch (error) {
        throw error;
      }
    },
    []
  );

  const deleteCharacter = useCallback(async (id: string) => {
    try {
      await firestoreService.deleteCharacter(id);
    } catch (error) {
      throw error;
    }
  }, []);

  return {
    createCharacter: createCharacterOperation,
    updateCharacter: updateCharacterOperation,
    deleteCharacter,
  };
};

export const useTitanOperations = () => {
  const createTitan = useCallback(
    async (titan: Omit<FirestoreTitan, "id" | "createdAt" | "updatedAt">) => {
      try {
        return await TitanService.createTitan({
          name: titan.name,
          description: titan.description,
          links: titan.links,
          levelRange: titan.levelRange,
          specialties: titan.specialties,
        });
      } catch (error) {
        throw error;
      }
    },
    []
  );

  const updateTitan = useCallback(
    async (id: string, updates: Partial<FirestoreTitan>) => {
      try {
        await TitanService.updateTitan({
          titanId: id,
          ...updates,
        });
      } catch (error) {
        throw error;
      }
    },
    []
  );

  const deleteTitan = useCallback(async (id: string) => {
    try {
      await TitanService.deleteTitan({titanId: id});
    } catch (error) {
      throw error;
    }
  }, []);

  return {createTitan, updateTitan, deleteTitan};
};

export const useUserOperations = () => {
  const updateUser = useCallback(
    async (uid: string, updates: Partial<UserProfile>) => {
      try {
        await firestoreService.updateUser(uid, updates);
      } catch (error) {
        throw error;
      }
    },
    []
  );

  const deleteUser = useCallback(async (uid: string) => {
    try {
      await firestoreService.deleteUser(uid);
    } catch (error) {
      throw error;
    }
  }, []);

  return {updateUser, deleteUser};
};

// Search hooks
export const useSessionSearch = (query: string) => {
  const [data, setData] = useState<FirestoreSession[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!query.trim()) {
      setData([]);
      return;
    }

    const searchSessions = async () => {
      try {
        setLoading(true);
        setError(null);
        const results = await firestoreService.searchSessions(query);
        setData(results);
      } catch (err) {
        setError(err instanceof Error ? err.message : "An error occurred");
      } finally {
        setLoading(false);
      }
    };

    const timeoutId = setTimeout(searchSessions, 300);
    return () => clearTimeout(timeoutId);
  }, [query]);

  return {data, loading, error};
};

export const useCharacterSearch = (query: string) => {
  const [data, setData] = useState<FirestoreCharacter[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!query.trim()) {
      setData([]);
      return;
    }

    const searchCharacters = async () => {
      try {
        setLoading(true);
        setError(null);
        const results = await firestoreService.searchCharacters(query);
        setData(results);
      } catch (err) {
        setError(err instanceof Error ? err.message : "An error occurred");
      } finally {
        setLoading(false);
      }
    };

    const timeoutId = setTimeout(searchCharacters, 300);
    return () => clearTimeout(timeoutId);
  }, [query]);

  return {data, loading, error};
};

export const useUserSearch = (query: string) => {
  const [data, setData] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!query.trim()) {
      setData([]);
      return;
    }

    const searchUsers = async () => {
      try {
        setLoading(true);
        setError(null);
        const results = await firestoreService.searchUsers(query);
        setData(results);
      } catch (err) {
        setError(err instanceof Error ? err.message : "An error occurred");
      } finally {
        setLoading(false);
      }
    };

    const timeoutId = setTimeout(searchUsers, 300);
    return () => clearTimeout(timeoutId);
  }, [query]);

  return {data, loading, error};
};

// Calendar-specific hook
export const useCalendarSessions = (filters?: {
  titanId?: string;
  minLevel?: number;
  maxLevel?: number;
}) => {
  const {data: sessions, loading, error} = useSessionsRealtime();
  const {data: users} = useUsersRealtime();

  // Helper to resolve DM ID to DM display name
  const getDMName = useCallback(
    (dmId: string): string => {
      if (!users || users.length === 0) {
        return dmId;
      }
      const dm = users.find((u: any) => u.uid === dmId);
      return dm ? dm.displayName : dmId;
    },
    [users]
  );

  // Parse level range from session's levelRange string (e.g., "Level 5-10" or "Level 1-20")
  const parseLevelRange = useCallback((levelRange: string) => {
    const match = levelRange.match(/Level\s+(\d+)-(\d+)/);
    if (match) {
      return {
        min: parseInt(match[1]),
        max: parseInt(match[2]),
      };
    }
    // Default to 1-20 if can't parse
    return {min: 1, max: 20};
  }, []);

  // Filter sessions based on provided filters
  const filteredSessions = useMemo(() => {
    if (!sessions || sessions.length === 0) return [];

    let filtered = [...sessions];

    // Filter by titan if specified
    if (filters?.titanId) {
      filtered = filtered.filter(
        (session) => session.titanId === filters.titanId
      );
    }

    // Filter by level range if specified
    if (filters?.minLevel !== undefined || filters?.maxLevel !== undefined) {
      const minLevel = filters.minLevel || 1;
      const maxLevel = filters.maxLevel || 20;

      filtered = filtered.filter((session) => {
        const sessionLevelRange = parseLevelRange(session.levelRange);

        // Check if there's overlap between filter range and session range
        // Session is included if its range overlaps with the filter range
        return !(
          sessionLevelRange.max < minLevel || sessionLevelRange.min > maxLevel
        );
      });
    }

    return filtered;
  }, [sessions, filters, parseLevelRange]);

  const transformToCalendarEvents = useCallback(
    (sessions: FirestoreSession[]) => {
      return sessions.map((session) => {
        // Use the same date conversion as Sessions page for consistency
        const sessionDate = convertToDate(session.date);

        // Create 4-hour session duration (typical D&D session length)
        const startDate = sessionDate;
        const endDate = new Date(startDate.getTime() + 4 * 60 * 60 * 1000);

        return {
          id: session.id || "",
          title: `${session.name} - ${getDMName(session.dmId)}`,
          start: startDate,
          end: endDate,
          resource: {
            ...session,
            // Pass the original session date to ensure consistent formatting
            // This matches exactly how Sessions page handles dates
            playersCount: session.players?.length || 0,
            spotsAvailable: session.maxPlayers - (session.players?.length || 0),
          },
        };
      });
    },
    [getDMName]
  );

  const events = useMemo(() => {
    if (!filteredSessions || filteredSessions.length === 0) return [];
    return transformToCalendarEvents(filteredSessions);
  }, [filteredSessions, transformToCalendarEvents]);

  return {
    events,
    loading,
    error,
  };
};
